import {
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Box, Flex, SkeletonText } from '@chakra-ui/react';
import { Transcript } from './Transcript';
import { Chapters } from './Chapters';
import { Participants } from './Participants';
import OptionHeader from '../component/OptionHeader';
import { EmptyResults } from '@/components/ats';
import { SVG_ASSERT_URL } from '@/constants/app-constants';
import { selectTranscriptTab } from '@/store/interview-video/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { setTranscriptTab } from '@/store/interview-video';

interface TabOption {
  value: string;
  label: string;
}

interface InterviewTranscriptPanelProps {
  data: any;
  currentTime: number;
  handleTimeJump: (_time: number, _shouldPlay?: boolean) => void;
  playerType?: 'mini' | 'modal';
  isLoading: boolean;
}

const tabOption = {
  Transcript,
  Chapters,
  Participant: Participants,
};

export const InterviewTranscriptPanel = forwardRef<
  any,
  InterviewTranscriptPanelProps
>(
  (
    {
      data,
      currentTime,
      handleTimeJump,
      playerType = 'mini',
      isLoading = false,
    }: InterviewTranscriptPanelProps,
    ref
  ) => {
    const headerOptions = useMemo<TabOption[]>(
      () => [
        { value: 'Transcript', label: 'Transcript' },
        { value: 'Chapters', label: 'Chapters' },
        { value: 'Participant', label: 'Participant' },
      ],
      []
    );
    const transcriptTab = useSelector(selectTranscriptTab);
    const dispatch = useDispatch();

    const tab = useMemo<TabOption>(
      () =>
        headerOptions.find((option) => option.value === transcriptTab) ||
        headerOptions[0],
      [transcriptTab, headerOptions]
    );
    const childRef = useRef<{
      triggerFunction: () => void;
      resetManualScroll?: () => void;
    }>(null);

    // Expose resetManualScroll method to parent
    const handleResetManualScroll = useCallback(() => {
      childRef.current?.resetManualScroll?.();
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        resetManualScroll: handleResetManualScroll,
      }),
      [handleResetManualScroll]
    );

    const Component = tabOption[tab?.value];

    const handleTabChange = useCallback(
      (value: TabOption) => {
        dispatch(setTranscriptTab(value.value));
      },
      [dispatch]
    );

    const handleClick = useCallback(() => {
      childRef.current?.triggerFunction();
    }, []);

    if (!data?.transcript?.length && !isLoading)
      return (
        <EmptyResults
          image={`${SVG_ASSERT_URL}/ats/no-applications.svg`}
          wrapperProps={{ h: '100%' }}
          text={'No transcript found'}
          iconProps={{ h: '140px', w: '142px', mb: '12px' }}
        />
      );

    return (
      <Flex
        flexDir="column"
        height="100%"
        minHeight="0"
        overflow="hidden"
        data-testid="interview-transcript-panel"
        width="100%"
      >
        <Flex p="12px" w="full">
          <OptionHeader
            onChange={handleTabChange}
            headerOptions={headerOptions}
            clickToCopy={handleClick}
            toShowCopy={tab?.value === 'Transcript'}
            initialValue={tab}
          />
        </Flex>
        {isLoading ? (
          <Box flex="1" p="12px">
            <SkeletonText
              mt="4"
              noOfLines={10}
              spacing="3"
              skeletonHeight="2"
            />
          </Box>
        ) : (
          <Flex
            p="0px 22px 10px"
            w="full"
            flex={1}
            minHeight="0"
            overflowY="auto"
          >
            <Component
              ref={childRef}
              currentTime={currentTime}
              data={data}
              handleTimeJump={handleTimeJump}
              playerType={playerType}
            />
          </Flex>
        )}
      </Flex>
    );
  }
);
